import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { getProductById } from '@/lib/supabase/products';

const MAX_PANNING_PRODUCT_CACHE = 250;

type CachedPanningProduct = {
  product: any;
  hydrated: boolean;
};

interface ContentSidebarContextType {
  noteSidebarOpen: boolean;
  createNoteSidebarOpen: boolean;
  productSidebarOpen: boolean;
  productSidebarFullscreen: boolean;
  showProductClickOverlay: boolean;
  showNoteClickOverlay: boolean;
  minimizedNotes: Record<string, boolean>;
  minimizedProducts: Record<string, boolean>;
  openedNotes: Array<{ id: string; title: string }>;
  openedProducts: Array<{ id: string; title: string }>;
  selectedProduct: any | null;
  selectedNote: any | null;
  productPanningActive: boolean;
  productPanningIndex: number;
  productPanningQueueLength: number;
  setNoteSidebarOpen: (open: boolean) => void;
  setCreateNoteSidebarOpen: (open: boolean) => void;
  setProductSidebarOpen: (open: boolean) => void;
  setProductSidebarFullscreen: (fullscreen: boolean) => void;
  productPanningIntervalMs: number;
  productPanningStepStartedAt: number;
  productPanningElapsedMs: number;
  setShowProductClickOverlay: (show: boolean) => void;
  setShowNoteClickOverlay: (show: boolean) => void;
  toggleNoteMinimized: (noteId: string) => void;
  toggleProductMinimized: (productId: string) => void;
  clearMinimizedNote: (noteId: string) => void;
  clearMinimizedProduct: (productId: string) => void;
  addOpenedNote: (id: string, title: string) => void;
  addOpenedProduct: (id: string, title: string) => void;
  removeOpenedNote: (id: string) => void;
  removeOpenedProduct: (id: string) => void;
  setSelectedProduct: (product: any | null) => void;
  setSelectedNote: (note: any | null) => void;
  startProductPanning: (products: any[], options?: { startIndex?: number; intervalMs?: number; loop?: boolean }) => void;
  pauseProductPanning: () => void;
  resumeProductPanning: (options?: { advanceToNext?: boolean }) => void;
  stopProductPanning: () => void;
  toggleProductPanningPlayback: () => void;
  goToNextPanningProduct: () => void;
  goToPreviousPanningProduct: () => void;
}

const ContentSidebarContext = createContext<ContentSidebarContextType | undefined>(undefined);

export const ContentSidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [noteSidebarOpen, setNoteSidebarOpen] = useState(false);
  const [createNoteSidebarOpen, setCreateNoteSidebarOpen] = useState(false);
  const [productSidebarOpen, setProductSidebarOpen] = useState(false);
  const [productSidebarFullscreen, setProductSidebarFullscreen] = useState(false);
  const [showProductClickOverlay, setShowProductClickOverlay] = useState(false);
  const [showNoteClickOverlay, setShowNoteClickOverlay] = useState(false);
  const [minimizedNotes, setMinimizedNotes] = useState<Record<string, boolean>>({});
  const [minimizedProducts, setMinimizedProducts] = useState<Record<string, boolean>>({});
  const [openedNotes, setOpenedNotes] = useState<Array<{ id: string; title: string }>>([]);
  const [openedProducts, setOpenedProducts] = useState<Array<{ id: string; title: string }>>([]);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [selectedNote, setSelectedNote] = useState<any | null>(null);
  const [productPanningActive, setProductPanningActive] = useState(false);
  const [productPanningQueue, setProductPanningQueue] = useState<any[]>([]);
  const [productPanningIndex, setProductPanningIndex] = useState(0);
  const [productPanningIntervalMs, setProductPanningIntervalMs] = useState(4500);
  const [productPanningLoop, setProductPanningLoop] = useState(true);
  const [productPanningStepStartedAt, setProductPanningStepStartedAt] = useState(0);
  const [productPanningElapsedMs, setProductPanningElapsedMs] = useState(0);
  const panningLoadTokenRef = useRef(0);
  const productCacheRef = useRef<Map<string, CachedPanningProduct>>(new Map());
  const inflightProductFetchesRef = useRef<Map<string, Promise<any>>>(new Map());
  const isHydratedProduct = useCallback((product: any | null | undefined) => {
    if (!product || typeof product !== 'object') return false;
    return Boolean(
      product.stores ||
      product.profiles ||
      product.product_chats ||
      product.rental_options ||
      product.current_bid !== undefined ||
      product.starting_bid !== undefined ||
      product.buy_now_price !== undefined
    );
  }, []);

  const cacheProduct = useCallback((product: any | null | undefined, options?: { hydrated?: boolean }) => {
    if (!product?.id) return;
    const cache = productCacheRef.current;
    const existing = cache.get(product.id);
    const hydrated = options?.hydrated ?? isHydratedProduct(product);
    if (existing?.hydrated && !hydrated) {
      return;
    }
    if (cache.has(product.id)) {
      cache.delete(product.id);
    }
    cache.set(product.id, { product, hydrated });
    if (cache.size > MAX_PANNING_PRODUCT_CACHE) {
      const oldestKey = cache.keys().next().value;
      if (oldestKey) {
        cache.delete(oldestKey);
      }
    }
  }, [isHydratedProduct]);

  const getCachedOrFetchProduct = useCallback(async (productOrId: any) => {
    const fallbackProduct = productOrId && typeof productOrId === 'object' ? productOrId : null;
    const productId = fallbackProduct?.id || productOrId;
    if (!productId) return fallbackProduct;

    const cached = productCacheRef.current.get(productId);
    if (cached?.hydrated) {
      return cached.product;
    }

    const inflight = inflightProductFetchesRef.current.get(productId);
    if (inflight) {
      const sharedProduct = await inflight;
      return sharedProduct || fallbackProduct;
    }

    const fetchPromise = (async () => {
      try {
        const fetched = await getProductById(productId);
        if (fetched) {
          cacheProduct(fetched, { hydrated: true });
          return fetched;
        }
        if (fallbackProduct) {
          cacheProduct(fallbackProduct, { hydrated: false });
        }
        return fallbackProduct;
      } finally {
        inflightProductFetchesRef.current.delete(productId);
      }
    })();

    inflightProductFetchesRef.current.set(productId, fetchPromise);
    const resolved = await fetchPromise;
    return resolved || fallbackProduct;
  }, [cacheProduct]);

  const prefetchPanningProduct = useCallback((productOrId: any) => {
    const productId = productOrId && typeof productOrId === 'object' ? productOrId.id : productOrId;
    if (!productId) return;
    if (productCacheRef.current.has(productId) || inflightProductFetchesRef.current.has(productId)) {
      return;
    }
    void getCachedOrFetchProduct(productOrId);
  }, [getCachedOrFetchProduct]);

  const _setProductSidebarOpen = (open: boolean) => {
    setProductSidebarOpen(open);
    if (!open) {
      setProductPanningActive(false);
    }
  };

  const _setSelectedProduct = (product: any | null) => {
    cacheProduct(product);
    setSelectedProduct(product);
  };

  const pauseProductPanning = useCallback(() => {
    if (productPanningActive && productPanningStepStartedAt > 0) {
      const elapsed = Math.min(productPanningIntervalMs, Math.max(0, Date.now() - productPanningStepStartedAt));
      setProductPanningElapsedMs(elapsed);
    }
    setProductPanningActive(false);
  }, [productPanningActive, productPanningIntervalMs, productPanningStepStartedAt]);

  const stopProductPanning = useCallback(() => {
    setProductPanningElapsedMs(0);
    setProductPanningStepStartedAt(0);
    setProductPanningActive(false);
  }, []);

  const resumeProductPanning = useCallback((options?: { advanceToNext?: boolean }) => {
    if (productPanningQueue.length === 0) return;

    const advanceToNext = options?.advanceToNext ?? false;
    if (advanceToNext && productPanningQueue.length > 1) {
      setProductPanningIndex((current) => {
        const atLast = current >= productPanningQueue.length - 1;
        if (atLast) {
          return productPanningLoop ? 0 : current;
        }
        return current + 1;
      });
    } else {
      setProductPanningStepStartedAt(Date.now() - productPanningElapsedMs);
    }

    setProductPanningActive(true);
  }, [productPanningElapsedMs, productPanningLoop, productPanningQueue.length]);

  const openPanningProduct = useCallback(async (queueItem: any) => {
    if (!queueItem?.id) return;
    const token = ++panningLoadTokenRef.current;
    cacheProduct(queueItem, { hydrated: false });
    const nextProduct = await getCachedOrFetchProduct(queueItem);

    if (token !== panningLoadTokenRef.current) return;
    setSelectedProduct(nextProduct);
    setProductSidebarOpen(true);
    setProductPanningElapsedMs(0);
    setProductPanningStepStartedAt(Date.now());
  }, [cacheProduct, getCachedOrFetchProduct]);

  const toggleProductPanningPlayback = useCallback(() => {
    if (productPanningQueue.length === 0) return;
    if (productPanningActive) {
      pauseProductPanning();
      return;
    }
    resumeProductPanning({ advanceToNext: true });
  }, [pauseProductPanning, productPanningActive, productPanningQueue.length, resumeProductPanning]);

  const startProductPanning = useCallback((products: any[], options?: { startIndex?: number; intervalMs?: number; loop?: boolean }) => {
    const queue = (products || []).filter((item) => item && typeof item === 'object' && item.id);
    if (queue.length === 0) {
      setProductPanningActive(false);
      return;
    }
    const requestedIndex = options?.startIndex ?? 0;
    const clampedIndex = Math.max(0, Math.min(requestedIndex, queue.length - 1));
    queue.forEach((item) => cacheProduct(item));
    setProductPanningQueue(queue);
    setProductPanningIndex(clampedIndex);
    setProductPanningIntervalMs(Math.max(1800, options?.intervalMs ?? 4500));
    setProductPanningLoop(options?.loop ?? true);
    setProductPanningElapsedMs(0);
    openPanningProduct(queue[clampedIndex]);
    setProductPanningActive(true);
  }, [cacheProduct, openPanningProduct]);

  const goToNextPanningProduct = useCallback(() => {
    setProductPanningIndex((prev) => {
      if (productPanningQueue.length === 0) return prev;
      const atLast = prev >= productPanningQueue.length - 1;
      if (atLast) {
        if (!productPanningLoop) {
          setProductPanningActive(false);
          return prev;
        }
        return 0;
      }
      return prev + 1;
    });
  }, [productPanningLoop, productPanningQueue.length]);

  const goToPreviousPanningProduct = useCallback(() => {
    setProductPanningIndex((prev) => {
      if (productPanningQueue.length === 0) return prev;
      const atFirst = prev <= 0;
      if (atFirst) {
        return productPanningLoop ? productPanningQueue.length - 1 : 0;
      }
      return prev - 1;
    });
  }, [productPanningLoop, productPanningQueue.length]);

  useEffect(() => {
    if (!productPanningQueue.length) return;
    const current = productPanningQueue[productPanningIndex];
    if (!current) return;
    openPanningProduct(current);
  }, [openPanningProduct, productPanningIndex, productPanningQueue]);

  useEffect(() => {
    if (productPanningQueue.length <= 1) return;
    const nextIndex = productPanningIndex >= productPanningQueue.length - 1
      ? (productPanningLoop ? 0 : -1)
      : productPanningIndex + 1;
    if (nextIndex < 0) return;
    prefetchPanningProduct(productPanningQueue[nextIndex]);
  }, [prefetchPanningProduct, productPanningIndex, productPanningLoop, productPanningQueue]);

  useEffect(() => {
    if (!productPanningActive || productPanningQueue.length <= 1 || !productPanningStepStartedAt) return;
    const elapsed = Date.now() - productPanningStepStartedAt;
    const remainingMs = Math.max(0, productPanningIntervalMs - elapsed);
    const timerId = window.setTimeout(() => {
      setProductPanningIndex((prev) => {
        const atLast = prev >= productPanningQueue.length - 1;
        if (atLast) {
          if (!productPanningLoop) {
            setProductPanningActive(false);
            return prev;
          }
          return 0;
        }
        return prev + 1;
      });
    }, remainingMs);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [productPanningActive, productPanningQueue.length, productPanningIntervalMs, productPanningLoop, productPanningStepStartedAt]);

  const toggleNoteMinimized = (noteId: string) => {
    setMinimizedNotes((prev) => {
      const newState = {
        ...prev,
        [noteId]: !prev[noteId],
      };
      return newState;
    });
  };

  const toggleProductMinimized = (productId: string) => {
    setMinimizedProducts((prev) => {
      const newState = {
        ...prev,
        [productId]: !prev[productId],
      };
      return newState;
    });
  };

  const clearMinimizedNote = (noteId: string) => {
    setMinimizedNotes((prev) => {
      const newState = { ...prev };
      delete newState[noteId];
      return newState;
    });
  };

  const clearMinimizedProduct = (productId: string) => {
    setMinimizedProducts((prev) => {
      const newState = { ...prev };
      delete newState[productId];
      return newState;
    });
  };

  const addOpenedNote = (id: string, title: string) => {
    setOpenedNotes((prev) => {
      const exists = prev.find((n) => n.id === id);
      if (exists) {
        return prev;
      }
      const newState = [...prev, { id, title }];
      return newState;
    });
  };

  const addOpenedProduct = (id: string, title: string) => {
    setOpenedProducts((prev) => {
      const exists = prev.find((p) => p.id === id);
      if (exists) {
        return prev;
      }
      const newState = [...prev, { id, title }];
      return newState;
    });
  };

  const removeOpenedNote = (id: string) => {
    setOpenedNotes((prev) => {
      const filtered = prev.filter((n) => n.id !== id);
      return filtered;
    });
  };

  const removeOpenedProduct = (id: string) => {
    setOpenedProducts((prev) => {
      const filtered = prev.filter((p) => p.id !== id);
      return filtered;
    });
  };

  const contextValue = useMemo(() => ({
    noteSidebarOpen,
    createNoteSidebarOpen,
    productSidebarOpen,
    productSidebarFullscreen,
    showProductClickOverlay,
    showNoteClickOverlay,
    minimizedNotes,
    minimizedProducts,
    openedNotes,
    openedProducts,
    selectedProduct,
    selectedNote,
    productPanningActive,
    productPanningIndex,
    productPanningQueueLength: productPanningQueue.length,
    productPanningIntervalMs,
    productPanningStepStartedAt,
    productPanningElapsedMs,
    setNoteSidebarOpen,
    setCreateNoteSidebarOpen,
    setProductSidebarOpen: _setProductSidebarOpen,
    setProductSidebarFullscreen,
    setShowProductClickOverlay,
    setShowNoteClickOverlay,
    toggleNoteMinimized,
    toggleProductMinimized,
    clearMinimizedNote,
    clearMinimizedProduct,
    addOpenedNote,
    addOpenedProduct,
    removeOpenedNote,
    removeOpenedProduct,
    setSelectedProduct: _setSelectedProduct,
    setSelectedNote,
    startProductPanning,
    pauseProductPanning,
    resumeProductPanning,
    stopProductPanning,
    toggleProductPanningPlayback,
    goToNextPanningProduct,
    goToPreviousPanningProduct,
  }), [
    noteSidebarOpen,
    createNoteSidebarOpen,
    productSidebarOpen,
    productSidebarFullscreen,
    showProductClickOverlay,
    showNoteClickOverlay,
    minimizedNotes,
    minimizedProducts,
    openedNotes,
    openedProducts,
    selectedProduct,
    selectedNote,
    productPanningActive,
    productPanningIndex,
    productPanningQueue.length,
    productPanningElapsedMs,
    productPanningIntervalMs,
    productPanningStepStartedAt,
    pauseProductPanning,
    resumeProductPanning,
    stopProductPanning,
    toggleProductPanningPlayback,
    startProductPanning,
    goToNextPanningProduct,
    goToPreviousPanningProduct,
  ]);

  return (
    <ContentSidebarContext.Provider
      value={contextValue}
    >
      {children}
    </ContentSidebarContext.Provider>
  );
};

export const useContentSidebar = () => {
  const context = useContext(ContentSidebarContext);
  if (context === undefined) {
    throw new Error('useContentSidebar must be used within a ContentSidebarProvider');
  }
  return context;
};
